import React, { useState, useMemo, useCallback, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import Back from "../common/back/Back"
import BlogCard from "./BlogCard"
import BlogPostDetail from "./BlogPostDetail"
import GraduationHighlights from './GraduationHighlights'
import { blog } from "../../dummydata"
import { useDebouncedValue } from "../../hooks/useDebouncedValue"
import "./blog.css"

const Blog = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [selectedPost, setSelectedPost] = useState(null)
  const [viewMode, setViewMode] = useState("grid") // grid | list
  const navigate = useNavigate()

  // Debounced search for performance with large datasets
  const debouncedSearch = useDebouncedValue(searchTerm, 350)

  // Get unique categories
  const categories = useMemo(() => {
    const cats = ["All", ...new Set(blog.map(post => post.category))]
    return cats
  }, [])

  // Graduation posts for highlights section
  const graduationPosts = useMemo(() => blog.filter(p => p.category === 'Graduation'), [])

  // Filter blog posts based on search and category
  const filteredBlog = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase()
    return blog.filter(post => {
      if (selectedCategory !== "All" && post.category !== selectedCategory) return false
      if (!needle) return true
      return (
        post.title.toLowerCase().includes(needle) ||
        post.desc.toLowerCase().includes(needle) ||
        post.category.toLowerCase().includes(needle)
      )
    })
  }, [debouncedSearch, selectedCategory])

  const handleReadMore = useCallback((post) => {
    setSelectedPost(post)
  }, [])

  const handleCloseModal = useCallback(() => {
    setSelectedPost(null)
  }, [])

  const handleViewAllPhotos = () => {
    navigate('/photos')
  }

  // Keyboard shortcut: Esc to close modal
  useEffect(() => {
    if (!selectedPost) return
    const onKey = (e) => {
      if (e.key === 'Escape') handleCloseModal()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [selectedPost, handleCloseModal])

  // Derived counts
  const totalCount = blog.length
  const resultCount = filteredBlog.length

  return (
    <>
      <Back title='Blog Posts' />
      
  {/* Graduation Highlights Section */}
  <GraduationHighlights posts={graduationPosts} onSelect={handleReadMore} />
      
  <section className='blog padding' aria-labelledby='blog-heading'>
        <div className='container'>
          {/* Search and Filter Section */}
          <div className='blog-controls'>
            <div className='search-section'>
              <div className='search-box'>
                <i className='fa fa-search'></i>
                <input
                  type="text"
                  placeholder="Search blog posts..."
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      aria-label='Search blog posts'
                />
              </div>
            </div>
            
            {/* Special Graduation Filter */}
      <div className='graduation-filter-section'>
              <button 
                className={`graduation-filter-btn ${selectedCategory === "Graduation" ? 'active' : ''}`}
                onClick={() => setSelectedCategory(selectedCategory === "Graduation" ? "All" : "Graduation")}
        aria-pressed={selectedCategory === 'Graduation'}
              >
                <i className='fa fa-graduation-cap'></i>
                {selectedCategory === "Graduation" ? "Show All Posts" : "Show Graduation Posts"}
              </button>
            </div>
            
            <div className='filter-section'>
              <div className='category-filters'>
                {categories.map((category) => {
                  const isActive = selectedCategory === category
                  return (
                    <button
                      key={category}
                      className={`category-btn ${isActive ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(category)}
                      aria-pressed={isActive}
                    >
                      {category}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* View Mode Toggle */}
            <div className='view-mode-toggle' role='group' aria-label='View mode'>
              <button
                className={`view-mode-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
                aria-pressed={viewMode === 'grid'}
              >
                <i className='fa fa-th'></i> Grid
              </button>
              <button
                className={`view-mode-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
                aria-pressed={viewMode === 'list'}
              >
                <i className='fa fa-list'></i> List
              </button>
            </div>
          </div>

          {/* Results Count */}
          <div className='results-info'>
            <p>Showing {resultCount} of {totalCount} blog posts {debouncedSearch && `for "${debouncedSearch}"`}</p>
          </div>

          {/* Blog Grid */}
          <div className={viewMode === 'grid' ? 'grid2' : 'list-view'}>
            {filteredBlog.length > 0 ? (
              filteredBlog.map((val) => (
                <BlogCard key={val.id} blog={val} onReadMore={handleReadMore} viewMode={viewMode} />
              ))
            ) : (
              <div className='no-results' role='alert'>
                <i className='fa fa-search'></i>
                <h3>No blog posts found</h3>
                <p>Try adjusting your search terms or category filter</p>
                {debouncedSearch && (
                  <button className='reset-search-btn' onClick={() => setSearchTerm("")}>Clear search</button>
                )}
              </div>
            )}
          </div>

          {/* View All Photos Button */}
          <div className='view-all-photos-section'>
            <button className='view-all-photos-btn' onClick={handleViewAllPhotos}>
              <i className='fa fa-images'></i>
              View All Photos
              <i className='fa fa-arrow-right'></i>
            </button>
            <p>Explore our complete photo collection from all programs and events</p>
          </div>
        </div>
      </section>

      {/* Blog Post Detail Modal */}
      {selectedPost && (
        <BlogPostDetail post={selectedPost} onClose={handleCloseModal} />
      )}
    </>
  )
}

export default Blog
